import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import client from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AuthedImage from "@/components/AuthedImage";
import RedactionEditor from "@/components/RedactionEditor";
import PlanPdfViewer from "@/components/PlanPdfViewer";
import {
  Ruler, Plus, Loader2, Trash2, Upload, ChevronLeft, ShieldCheck, Sparkles, Send,
  CheckCircle2, RotateCcw, FileText, Building2, MapPin, Phone, User, Clock, Eye, Download,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const PRIORITY_META = {
  high: { label: "High", cls: "bg-error/10 text-error" },
  normal: { label: "Normal", cls: "bg-primary/10 text-primary" },
  low: { label: "Low", cls: "bg-slate-200/70 text-slate-600" },
};
const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-200/70 text-slate-600" },
  assigned: { label: "With estimator", cls: "bg-[#D97706]/12 text-[#D97706]" },
  submitted: { label: "Submitted", cls: "bg-[#0284C7]/12 text-[#0284C7]" },
  approved: { label: "Approved", cls: "bg-[#059669]/12 text-[#059669]" },
};
const TASK_META = { tiles: "Tiles", hybrid_flooring: "Hybrid flooring", other: "Other" };

function Badge({ meta }) {
  return <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold", meta?.cls)}>{meta?.label}</span>;
}

function TaskRows({ tasks, setTasks, editable }) {
  const add = () => setTasks([...tasks, { id: "task_" + Math.random().toString(36).slice(2, 8), type: "tiles", area_label: "", instructions: "" }]);
  const upd = (i, k, v) => setTasks(tasks.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const del = (i) => setTasks(tasks.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid="task-rows">
      {tasks.map((t, i) => (
        <div key={t.id || i} className="rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={t.type} onValueChange={(v) => upd(i, "type", v)} disabled={!editable}>
              <SelectTrigger className="h-9 w-40" data-testid={`task-type-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tiles">Tiles</SelectItem>
                <SelectItem value="hybrid_flooring">Hybrid flooring</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Input value={t.area_label} onChange={(e) => upd(i, "area_label", e.target.value)} disabled={!editable}
              placeholder="Areas e.g. Kitchen, Ensuite, Laundry" data-testid={`task-area-${i}`} className="min-w-[180px] flex-1" />
            {editable && <button onClick={() => del(i)} data-testid={`task-del-${i}`} className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-muted-foreground hover:border-error/40 hover:text-error"><Trash2 className="h-4 w-4" /></button>}
          </div>
          <Textarea value={t.instructions} onChange={(e) => upd(i, "instructions", e.target.value)} disabled={!editable}
            placeholder="What to calculate (e.g. floor + wall tiling m², include skirting)" data-testid={`task-instructions-${i}`} className="mt-2 min-h-[52px] text-sm" />
        </div>
      ))}
      {editable && <Button variant="outline" size="sm" onClick={add} data-testid="add-task-btn" className="gap-1.5"><Plus className="h-4 w-4" />Add a task</Button>}
    </div>
  );
}

function CreateDialog({ open, onOpenChange, onCreated }) {
  const [title, setTitle] = useState("");
  const [bName, setBName] = useState("");
  const [bAddr, setBAddr] = useState("");
  const [bContact, setBContact] = useState("");
  const [bPhone, setBPhone] = useState("");
  const [priority, setPriority] = useState("normal");
  const [tasks, setTasks] = useState([{ id: "task_1", type: "tiles", area_label: "", instructions: "" }]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Give it an internal title"); return; }
    setSaving(true);
    try {
      const { data } = await client.post("/takeoffs", {
        title, builder_name: bName, builder_address: bAddr, builder_contact: bContact, builder_phone: bPhone, priority, tasks,
      });
      toast.success("Takeoff created — now upload the plan");
      onCreated(data.id);
      onOpenChange(false);
      setTitle(""); setBName(""); setBAddr(""); setBContact(""); setBPhone(""); setPriority("normal");
      setTasks([{ id: "task_1", type: "tiles", area_label: "", instructions: "" }]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto" data-testid="create-takeoff-dialog">
        <DialogHeader><DialogTitle className="font-display">New takeoff</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block text-xs">Internal title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Lot 42 Riverstone — Frankie Homes" data-testid="new-title-input" />
          </div>
          <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-amber-800"><ShieldCheck className="h-3.5 w-3.5" />Private — hidden from the estimator</div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Input value={bName} onChange={(e) => setBName(e.target.value)} placeholder="Builder / client name" data-testid="new-builder-name" />
              <Input value={bPhone} onChange={(e) => setBPhone(e.target.value)} placeholder="Phone" data-testid="new-builder-phone" />
            </div>
            <Input value={bAddr} onChange={(e) => setBAddr(e.target.value)} placeholder="Site address" data-testid="new-builder-address" className="mt-2" />
            <Input value={bContact} onChange={(e) => setBContact(e.target.value)} placeholder="Contact person / notes" data-testid="new-builder-contact" className="mt-2" />
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="w-40" data-testid="new-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block text-xs">What needs doing</Label>
            <TaskRows tasks={tasks} setTasks={setTasks} editable />
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={saving} data-testid="create-takeoff-submit" className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Create
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TakeoffList() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const list = useQuery({ queryKey: ["takeoffs"], queryFn: async () => (await client.get("/takeoffs")).data });
  const items = list.data || [];

  return (
    <div className="animate-fade-up">
      <PageHeader title="Takeoffs" subtitle="Send redacted plans to your estimator for tile & flooring take-offs" testid="takeoffs-header" />
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setOpen(true)} data-testid="new-takeoff-btn" className="gap-2"><Plus className="h-4 w-4" />New takeoff</Button>
      </div>
      <CreateDialog open={open} onOpenChange={setOpen} onCreated={(id) => navigate(`/takeoffs/${id}`)} />
      {list.isLoading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-muted/50" />
      ) : items.length === 0 ? (
        <EmptyState icon={Ruler} title="No takeoffs yet" hint="Create a takeoff, upload the plan, black out the private info and assign it to your estimator." />
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="takeoffs-list">
          {items.map((t) => (
            <button key={t.id} onClick={() => navigate(`/takeoffs/${t.id}`)} data-testid={`takeoff-card-${t.id}`}
              className="rounded-2xl border border-slate-100 bg-white p-4 text-left shadow-[0_2px_12px_rgba(35,52,90,0.04)] transition-all hover:border-primary/30 hover:shadow-[0_4px_18px_rgba(35,52,90,0.10)]">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent px-2.5 py-0.5 font-mono text-xs font-bold text-accent-foreground">{t.ref}</span>
                <Badge meta={PRIORITY_META[t.priority]} />
                <Badge meta={STATUS_META[t.status]} />
              </div>
              <div className="mt-2 font-display text-base font-bold text-ink">{t.title}</div>
              {t.builder_name && <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground"><Building2 className="h-3 w-3" />{t.builder_name}</div>}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(t.tasks || []).map((tk) => <span key={tk.id} className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-ink">{TASK_META[tk.type]}{tk.area_label ? `: ${tk.area_label}` : ""}</span>)}
              </div>
              <div className="mt-2 text-xs text-muted-foreground">{t.plan_filename ? `${t.shared_pages?.length || 0} of ${t.page_count} pages shared` : "No plan uploaded"}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TakeoffDetail({ id }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const [selPages, setSelPages] = useState([]);
  const [redaction, setRedaction] = useState(null); // local editable copy keyed by shared index
  const [showPdf, setShowPdf] = useState(false);  const [savingMeta, setSavingMeta] = useState(false);
  const [tasks, setTasks] = useState(null);

  const q = useQuery({ queryKey: ["takeoff", id], queryFn: async () => (await client.get(`/takeoffs/${id}`)).data });
  const t = q.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["takeoff", id] });

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await client.post(`/takeoffs/${id}/plan`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Plan uploaded"); setSelPages([]); setRedaction(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const togglePage = (n) => setSelPages((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n].sort((a, b) => a - b)));

  const analyze = async () => {
    if (!selPages.length) { toast.error("Pick the pages the estimator should see"); return; }
    setAnalyzing(true);
    try {
      const { data } = await client.post(`/takeoffs/${id}/analyze`, { pages: selPages });
      setRedaction(data.redaction);
      toast.success("AI suggested black-out boxes — review then publish");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not prepare pages"); }
    finally { setAnalyzing(false); }
  };

  const saveRedaction = async (rmap) => {
    try { await client.put(`/takeoffs/${id}/redaction`, { redaction: rmap }); } catch { /* ignore */ }
  };
  const setBoxesFor = (idx, boxes) => {
    const next = { ...(redaction || {}), [idx]: boxes };
    setRedaction(next);
    saveRedaction(next);
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await client.post(`/takeoffs/${id}/publish`, { confirm: true });
      toast.success("Redacted plan published & assigned to the estimator");
      setConfirmOpen(false); setRedaction(null); setSelPages([]); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
    finally { setPublishing(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await client.get(`/takeoffs/${id}/redacted-pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u; a.download = `${t.ref}-redacted-plan.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 2000);
    } catch { toast.error("Could not download the PDF"); }
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try { await client.put(`/takeoffs/${id}`, { tasks: tasks ?? t.tasks }); toast.success("Saved"); setTasks(null); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSavingMeta(false); }
  };

  const doApprove = async () => { try { await client.post(`/takeoffs/${id}/approve`); toast.success("Approved"); refresh(); } catch (e) { toast.error("Failed"); } };
  const doReopen = async () => { try { await client.post(`/takeoffs/${id}/reopen`); toast.success("Sent back to estimator"); refresh(); } catch (e) { toast.error("Failed"); } };
  const del = async () => { if (!window.confirm("Delete this takeoff and its plan?")) return; try { await client.delete(`/takeoffs/${id}`); toast.success("Deleted"); navigate("/takeoffs"); } catch { toast.error("Failed"); } };

  if (q.isLoading) return <div className="h-64 animate-pulse rounded-2xl bg-muted/50" />;
  if (!t) return null;

  const prepping = redaction !== null;
  const canEditPlan = t.status === "draft";
  const resultsByTask = Object.fromEntries((t.results || []).map((r) => [r.task_id, r]));

  return (
    <div className="animate-fade-up space-y-5">
      <button onClick={() => navigate("/takeoffs")} data-testid="back-to-takeoffs" className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-ink"><ChevronLeft className="h-4 w-4" />All takeoffs</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-accent px-2.5 py-0.5 font-mono text-xs font-bold text-accent-foreground">{t.ref}</span>
            <Badge meta={PRIORITY_META[t.priority]} /><Badge meta={STATUS_META[t.status]} />
          </div>
          <h1 className="mt-2 font-display text-2xl font-bold text-ink" data-testid="takeoff-title">{t.title}</h1>
        </div>
        <div className="flex items-center gap-2">
          {t.status === "submitted" && <Button onClick={doApprove} data-testid="approve-btn" className="gap-1.5 bg-[#059669] hover:bg-[#047857]"><CheckCircle2 className="h-4 w-4" />Approve</Button>}
          {(t.status === "submitted" || t.status === "approved") && <Button onClick={doReopen} variant="outline" data-testid="reopen-btn" className="gap-1.5"><RotateCcw className="h-4 w-4" />Reopen</Button>}
          <Button onClick={del} variant="outline" data-testid="delete-takeoff-btn" className="gap-1.5 border-error/30 text-error hover:bg-error/10"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Private builder info */}
      <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4" data-testid="private-info">
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-amber-800"><ShieldCheck className="h-3.5 w-3.5" />Private — never shown to the estimator</div>
        <div className="grid gap-1.5 text-sm text-ink sm:grid-cols-2">
          {t.builder_name && <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{t.builder_name}</span>}
          {t.builder_phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{t.builder_phone}</span>}
          {t.builder_address && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-muted-foreground" />{t.builder_address}</span>}
          {t.builder_contact && <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5 text-muted-foreground" />{t.builder_contact}</span>}
        </div>
      </div>

      {/* Tasks */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-display text-sm font-bold text-ink">Scope of work</h2>
          {tasks !== null && <Button size="sm" onClick={saveMeta} disabled={savingMeta} data-testid="save-tasks-btn" className="gap-1.5">{savingMeta ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}Save</Button>}
        </div>
        <TaskRows tasks={tasks ?? t.tasks} setTasks={setTasks} editable />
      </div>

      {/* Plan + redaction */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4">
        <h2 className="mb-3 font-display text-sm font-bold text-ink">Plan</h2>
        {!t.plan_filename ? (
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-4 py-10 text-center hover:border-primary/40" data-testid="plan-drop">
            <Upload className="h-6 w-6 text-muted-foreground" />
            <span className="text-sm font-semibold text-ink">Upload the plan PDF</span>
            <span className="text-xs text-muted-foreground">Then pick which pages to share (max 8)</span>
            <input type="file" accept="application/pdf" className="hidden" data-testid="plan-input" onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} />
            {uploading && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          </label>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-primary" /><span className="font-medium text-ink">{t.plan_filename}</span>
              <span className="text-muted-foreground">· {t.page_count} pages</span>
              {canEditPlan && (
                <label className="ml-auto cursor-pointer text-xs font-semibold text-primary hover:underline" data-testid="replace-plan">
                  Replace<input type="file" accept="application/pdf" className="hidden" onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              )}
            </div>

            {canEditPlan && !prepping && (
              <>
                <div>
                  <Label className="mb-2 block text-xs">Select the pages the estimator needs (tap to toggle)</Label>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="page-picker">
                    {Array.from({ length: t.page_count }).map((_, n) => (
                      <button key={n} onClick={() => togglePage(n)} data-testid={`page-thumb-${n}`}
                        className={cn("relative overflow-hidden rounded-lg border-2 transition-all", selPages.includes(n) ? "border-primary ring-2 ring-primary/30" : "border-slate-200 hover:border-slate-300")}>
                        <AuthedImage src={`/takeoffs/${id}/thumb/${n}`} className="block aspect-[3/4] w-full object-cover" />
                        <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 text-[10px] font-bold text-white">{n + 1}</span>
                        {selPages.includes(n) && <span className="absolute right-1 top-1 grid h-4 w-4 place-items-center rounded-full bg-primary text-white"><CheckCircle2 className="h-3 w-3" /></span>}
                      </button>
                    ))}
                  </div>
                </div>
                <Button onClick={analyze} disabled={analyzing || !selPages.length} data-testid="analyze-btn" className="gap-2">
                  {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {analyzing ? "AI scanning pages…" : `Prepare ${selPages.length || ""} page${selPages.length === 1 ? "" : "s"} (AI redaction)`}
                </Button>
              </>
            )}

            {prepping && (
              <div className="space-y-4">
                <div className="rounded-lg bg-primary/5 px-3 py-2 text-xs text-primary">Review the AI black-out boxes on each page. Add any it missed, then publish. The estimator only ever gets the flattened, redacted image.</div>
                {t.shared_pages.map((orig, idx) => (
                  <div key={idx} data-testid={`redaction-page-${idx}`}>
                    <div className="mb-1.5 text-xs font-semibold text-ink">Plan page {orig + 1}</div>
                    <RedactionEditor imgSrc={`/takeoffs/${id}/source-page/${orig}`} boxes={(redaction || {})[idx] || []} onChange={(b) => setBoxesFor(idx, b)} />
                  </div>
                ))}
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => { setAck(false); setConfirmOpen(true); }} disabled={publishing} data-testid="publish-btn" className="gap-2 bg-[#059669] hover:bg-[#047857]">
                    {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}Publish & assign to estimator
                  </Button>
                  <Button variant="outline" onClick={() => { setRedaction(null); }} data-testid="cancel-prep-btn">Back to page selection</Button>
                </div>
              </div>
            )}

            {!canEditPlan && (
              <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid="redacted-pdf-panel">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm">
                    <ShieldCheck className="h-4 w-4 shrink-0 text-[#059669]" />
                    <span className="font-medium text-ink">{t.shared_pages.length} redacted page{t.shared_pages.length === 1 ? "" : "s"} shared with the estimator (HD PDF)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => setShowPdf(true)} data-testid="view-hd-pdf-btn" className="h-8 gap-1.5 text-xs"><Eye className="h-3.5 w-3.5" />View PDF</Button>
                    <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="download-hd-pdf-btn" className="h-8 gap-1.5 text-xs"><Download className="h-3.5 w-3.5" />Download</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Results */}
      {(t.results?.length > 0 || t.estimator_notes) && (
        <div className="rounded-2xl border border-slate-100 bg-white p-4" data-testid="results-panel">
          <h2 className="mb-3 font-display text-sm font-bold text-ink">Estimator results</h2>
          <div className="space-y-2">
            {(t.tasks || []).map((tk) => {
              const r = resultsByTask[tk.id];
              return (
                <div key={tk.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-sm">
                  <span className="font-medium text-ink">{TASK_META[tk.type]}{tk.area_label ? `: ${tk.area_label}` : ""}</span>
                  <span className="font-mono font-bold text-primary">{r?.value != null && r?.value !== "" ? `${r.value} ${r.unit || "m²"}` : "—"}</span>
                  {r?.notes && <span className="w-full text-xs text-muted-foreground">{r.notes}</span>}
                </div>
              );
            })}
          </div>
          {t.estimator_notes && <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-ink"><span className="font-semibold">Notes: </span>{t.estimator_notes}</p>}
        </div>
      )}

      {/* Activity log */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4">
        <h2 className="mb-3 flex items-center gap-1.5 font-display text-sm font-bold text-ink"><Clock className="h-4 w-4" />Activity log</h2>
        <div className="space-y-2" data-testid="activity-log">
          {(t.logs || []).slice().reverse().map((l) => (
            <div key={l.id} className="flex items-start gap-2 text-xs">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary/60" />
              <div>
                <span className="font-medium text-ink">{l.summary}</span>
                <span className="ml-2 text-muted-foreground">{l.actor_name} · {new Date(l.at).toLocaleString()}</span>
              </div>
            </div>
          ))}
          {(t.logs || []).length === 0 && <p className="text-xs text-muted-foreground">No activity yet.</p>}
        </div>
      </div>

      <Dialog open={showPdf} onOpenChange={setShowPdf}>
        <DialogContent className="max-w-4xl w-[calc(100%-1.5rem)]" data-testid="hd-pdf-dialog">
          <DialogHeader><DialogTitle className="font-display">Redacted plan (HD) — {t.ref}</DialogTitle></DialogHeader>
          <PlanPdfViewer src={`/takeoffs/${id}/redacted-pdf`} filename={`${t.ref}-redacted-plan.pdf`} heightClass="h-[72vh]" />
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md w-[calc(100%-1.5rem)]" data-testid="publish-confirm-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><ShieldCheck className="h-5 w-5 text-[#059669]" />Confirm before sharing</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">Once published, the estimator can view these plan pages. Anything not blacked out will be visible to them.</p>
            {(() => {
              const rmap = redaction || {};
              const empty = (t.shared_pages || []).map((orig, idx) => ({ orig, idx })).filter((p) => !((rmap[p.idx]) || []).length);
              return empty.length ? (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800" data-testid="empty-pages-warning">
                  <span className="font-semibold">Heads up:</span> plan page{empty.length > 1 ? "s" : ""} {empty.map((p) => p.orig + 1).join(", ")} {empty.length > 1 ? "have" : "has"} no black-out boxes. Go back and add boxes if there's any private info on {empty.length > 1 ? "them" : "it"}.
                </div>
              ) : (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-700">Every shared page has at least one black-out box.</div>
              );
            })()}
            <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2" data-testid="publish-ack">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="publish-ack-checkbox" className="mt-0.5 h-4 w-4" />
              <span className="text-ink">I've reviewed every shared page and confirm all private / identifying info (address, builder, phone, names, logos) is blacked out.</span>
            </label>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="publish-cancel">Cancel</Button>
            <Button onClick={publish} disabled={!ack || publishing} data-testid="publish-confirm-btn" className="gap-2 bg-[#059669] hover:bg-[#047857]">
              {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}Publish & assign
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Takeoffs() {
  const { takeoffId } = useParams();
  return takeoffId ? <TakeoffDetail id={takeoffId} /> : <TakeoffList />;
}
